import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiInfo, FiX } from "react-icons/fi";
import { formatBytes } from "./attachmentState";

// Same modal shell as components/usageBilling/MyUsageModal.js, so the notice looks like the rest of the app.
// Rendered into document.body for the same reason: fixed overlays inside transformed containers get trapped.

function minutes(seconds) {
  return Math.round(seconds / 60);
}

function pixels(value, zh) {
  return zh
    ? `${(value / 10000).toLocaleString("zh-TW")} 萬像素`
    : `${(value / 1000000).toLocaleString("en-US")} million pixels`;
}

// "Why these limits?" in the app's own words. Root sends each notice's code,
// and the numbers in capability.limits; the prose it also sends is internal
// notes, in Chinese only, so it isn't shown. A code not listed here falls back
// to Root's message, so a limit added later still appears.
const NOTICE_TEXT = {
  FILE_BYTES: (limits, zh) =>
    zh
      ? {
          message: `圖片單檔最多 ${formatBytes(limits.imageFileBytes)}，PDF 單檔最多 ${formatBytes(limits.pdfFileBytes)}。`,
          reason: "讓每個檔案都在 AI 模型一次能讀取的範圍內。",
        }
      : {
          message: `Each image can be up to ${formatBytes(limits.imageFileBytes)}, and each PDF up to ${formatBytes(limits.pdfFileBytes)}.`,
          reason:
            "This keeps every file within what the AI model can read in one go.",
        },
  TURN_BYTES: (limits, zh) =>
    zh
      ? {
          message: `每次提問附上的檔案合計最多 ${formatBytes(limits.turnBytes)}，超過時請取消部分檔案。`,
          reason: "檔案會和問題一起送給 AI 模型，而單次請求的大小有上限。",
        }
      : {
          message: `The files sent with one question can add up to ${formatBytes(limits.turnBytes)}; deselect some if you go over.`,
          reason:
            "The files go to the AI model together with your question, and one request can only be so large.",
        },
  SESSION_BYTES: (limits, zh) =>
    zh
      ? {
          message: `同一段對話的檔案合計最多 ${formatBytes(limits.sessionBytes)}。`,
          reason: "剛移除的檔案在清理完成前仍會暫時計入。",
        }
      : {
          message: `All the files in one chat can add up to ${formatBytes(limits.sessionBytes)}.`,
          reason:
            "A file you just removed still counts until it has been cleaned up.",
        },
  IMAGE_PIXELS: (limits, zh) =>
    zh
      ? {
          message: `圖片最多 ${pixels(limits.imagePixels, true)}。`,
          reason: "過大的圖片在準備給 AI 模型時會佔用太多記憶體。",
        }
      : {
          message: `An image can be up to ${pixels(limits.imagePixels, false)}.`,
          reason:
            "Very large images take too much memory to prepare for the AI model.",
        },
  // The size of the upload request itself: a server detail the per-file limit already covers.
  REQUEST_BYTES: () => null,
};

function noticeText(notice, limits, zh) {
  const text = NOTICE_TEXT[notice.code];
  if (text) return text(limits, zh);
  return notice.message ? { message: notice.message } : null;
}

function Row({ label, children }) {
  return (
    <div className="flex gap-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <dt className="w-24 shrink-0 text-sm font-medium text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

function SearchLines({ routes, zh }) {
  const values = Object.values(routes || {});
  if (!values.length) return null;
  return values.map((route) =>
    route.service === "faq" ? (
      <p key="faq">
        Gemini：
        {zh
          ? "必要時以短詞查詢 FAQ，結果可能來自 FAQ 資料庫或網路摘要。"
          : "may look up short keywords in our FAQ; results can come from the FAQ database or a web summary."}
      </p>
    ) : (
      <p key={route.service}>
        GPT：
        {zh
          ? "必要時以所選 GPT 自身的 web_search 查詢短詞。"
          : "may search short keywords with its own web_search."}
      </p>
    ),
  );
}

// mode "upload": shown when someone starts an upload; "info": opened from the menu, read-only.
export default function UploadNoticeModal({
  capability,
  language,
  files = [],
  mode,
  onConfirm,
  onClose,
}) {
  const zh = language === "zh";
  const [dontRemind, setDontRemind] = useState(false);
  const confirmButton = useRef(null);
  const { limits, lifetime } = capability;
  const notes = (capability.notices || [])
    .map((notice) => {
      const text = noticeText(notice, limits, zh);
      return text && { code: notice.code, ...text };
    })
    .filter(Boolean);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    confirmButton.current?.focus();
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const title = zh ? "上傳須知" : "Before you upload";
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-notice-title"
        className="w-full max-w-lg max-h-[90vh] flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 animate-scaleIn"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 p-2 rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-300">
              <FiInfo className="w-4 h-4" />
            </span>
            <div>
              <h2
                id="upload-notice-title"
                className="text-lg font-bold text-gray-900 dark:text-white"
              >
                {title}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {files.length > 0
                  ? files.map((file) => file.name).join("、")
                  : zh
                    ? "圖片與 PDF 會交給你選的 AI 模型閱讀"
                    : "Images and PDFs are read by the AI model you picked"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label={zh ? "關閉上傳須知" : "Close"}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-3">
          <dl>
            <Row label={zh ? "格式" : "Formats"}>PNG、JPEG、WEBP、PDF</Row>
            <Row label={zh ? "單檔大小" : "File size"}>
              {zh ? "圖片" : "Image"} ≤ {formatBytes(limits.imageFileBytes)}
              ；PDF ≤ {formatBytes(limits.pdfFileBytes)}
            </Row>
            <Row label={zh ? "每輪／每對話" : "Per turn / chat"}>
              {zh ? "每次提問合計" : "Per question"} ≤{" "}
              {formatBytes(limits.turnBytes)}；
              {zh ? "整個對話合計" : "whole chat"} ≤{" "}
              {formatBytes(limits.sessionBytes)}
            </Row>
            <Row label={zh ? "保留時間" : "Retention"}>
              {zh
                ? `閒置 ${minutes(lifetime.idleSeconds)} 分鐘後自動移除（最長 ${Math.round(lifetime.absoluteSeconds / 3600)} 小時），也可隨時手動移除。`
                : `Removed after ${minutes(lifetime.idleSeconds)} idle minutes (at most ${Math.round(lifetime.absoluteSeconds / 3600)} hours); you can remove a file at any time.`}
            </Row>
            <Row label={zh ? "網路查詢" : "Web lookup"}>
              <SearchLines routes={capability.search?.routes} zh={zh} />
            </Row>
          </dl>
          {notes.length > 0 && (
            <details className="mt-3 text-sm text-gray-600 dark:text-gray-300">
              <summary className="cursor-pointer font-medium text-gray-700 dark:text-gray-200">
                {zh ? "為什麼有這些限制？" : "Why these limits?"}
              </summary>
              <ul className="mt-2 space-y-2">
                {notes.map((note) => (
                  <li key={note.code}>
                    <p>{note.message}</p>
                    {note.reason && (
                      <p className="text-gray-500 dark:text-gray-400">
                        {note.reason}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
          {mode === "upload" ? (
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={dontRemind}
                onChange={(event) => setDontRemind(event.target.checked)}
                className="accent-orange-500"
              />
              {zh ? "下次不再提醒" : "Don't remind me again"}
            </label>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            {mode === "upload" && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm rounded-lg text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                {zh ? "取消" : "Cancel"}
              </button>
            )}
            <button
              ref={confirmButton}
              type="button"
              onClick={() =>
                mode === "upload" ? onConfirm(dontRemind) : onClose()
              }
              className="px-4 py-2 text-sm font-medium rounded-lg bg-orange-500 hover:bg-orange-600 text-white transition-colors"
            >
              {mode === "upload"
                ? zh
                  ? "了解，繼續上傳"
                  : "Got it, continue"
                : zh
                  ? "關閉"
                  : "Close"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
